import { useEffect, useState } from "react";
import { Plus, Loader2, Receipt, Trash2 } from "lucide-react";
import api, { money, fmtDate } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const CATEGORIES = ["Parts", "Tools", "Supplies", "Rent", "Utilities", "Fuel", "Insurance", "Other"];

export default function Expenses() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", vendor: "", category: "Parts", amount: "", date: "", notes: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/expenses"); setItems(data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      await api.post("/expenses", { ...form, amount: Number(form.amount || 0) });
      toast.success("Expense added");
      setOpen(false);
      setForm({ title: "", vendor: "", category: "Parts", amount: "", date: "", notes: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/expenses/${id}`); setItems((x) => x.filter((e) => e.id !== id)); toast.success("Expense deleted"); }
    catch { toast.error("Failed to delete"); }
  };

  const total = items.reduce((s, e) => s + Number(e.amount || 0), 0);
  const field = "h-10 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50";

  return (
    <Layout
      title="Expenses"
      action={<Button data-testid="new-expense-button" onClick={() => setOpen(true)} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"><Plus className="h-4 w-4 mr-1.5" /> Add Expense</Button>}
    >
      <div className="space-y-4 animate-fade-up">
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-5 inline-flex items-center gap-4">
          <div className="h-11 w-11 rounded-lg bg-rose-500/15 flex items-center justify-center"><Receipt className="h-5 w-5 text-rose-400" /></div>
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Total Expenses</p>
            <p className="font-head font-black text-2xl text-slate-50 tabular-nums" data-testid="expenses-total">{money(total)}</p>
          </div>
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
          ) : items.length === 0 ? (
            <div className="p-16 text-center"><Receipt className="h-10 w-10 text-slate-700 mx-auto mb-3" /><p className="text-slate-400">No expenses logged yet.</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-950/50 text-slate-500 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left font-semibold px-4 py-3">Title</th>
                    <th className="text-left font-semibold px-4 py-3 hidden sm:table-cell">Vendor</th>
                    <th className="text-left font-semibold px-4 py-3">Category</th>
                    <th className="text-left font-semibold px-4 py-3 hidden md:table-cell">Date</th>
                    <th className="text-right font-semibold px-4 py-3">Amount</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/70">
                  {items.map((e) => (
                    <tr key={e.id} data-testid={`expense-row-${e.id}`} className="hover:bg-slate-800/40 transition-colors duration-150">
                      <td className="px-4 py-3.5 font-medium text-slate-100">{e.title}</td>
                      <td className="px-4 py-3.5 text-slate-400 hidden sm:table-cell">{e.vendor || "—"}</td>
                      <td className="px-4 py-3.5"><span className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-300">{e.category}</span></td>
                      <td className="px-4 py-3.5 text-slate-400 hidden md:table-cell">{fmtDate(e.date || e.created_at)}</td>
                      <td className="px-4 py-3.5 text-right font-bold text-rose-400 tabular-nums">{money(e.amount)}</td>
                      <td className="px-4 py-3.5 text-right">
                        <Button variant="ghost" size="icon" data-testid={`delete-expense-${e.id}`} onClick={() => remove(e.id)} className="h-8 w-8 text-slate-500 hover:text-rose-400 hover:bg-slate-800"><Trash2 className="h-4 w-4" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-slate-100 font-head">Add Expense</DialogTitle>
            <DialogDescription className="text-slate-500">Log a shop cost to track profit accurately.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Title *</Label><Input data-testid="expense-title-input" value={form.title} onChange={set("title")} className={field} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Vendor</Label><Input value={form.vendor} onChange={set("vendor")} className={field} /></div>
              <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Amount</Label><Input data-testid="expense-amount-input" type="number" step="0.01" value={form.amount} onChange={set("amount")} className={field} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="expense-category-select" className="h-10 bg-slate-950 border-slate-700"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-200">{CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Date</Label><Input type="date" value={form.date} onChange={set("date")} className={field} /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Notes</Label><Input value={form.notes} onChange={set("notes")} className={field} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700">Cancel</Button>
            <Button data-testid="save-expense-button" onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
              {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
